import { FormEvent, useEffect, useRef, useState, type CSSProperties } from 'react'
import { Skeleton, useSkeletonDemo } from '../components/Skeleton'
import { useCatalog } from '../context/CatalogContext'
import { useAuth } from '../context/AuthContext'
import { Spinner } from '../components/Spinner'
import {
  clearProductStation,
  saveTenantSettings,
  saveCategory,
  reorderCategories,
  moveProducts,
  saveProduct,
  setProductActive,
  deleteProduct,
  deleteCategory,
} from '../services/products'
import {
  GripVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  PencilIcon,
  PlusIcon,
  NotebookPenIcon,
  ReceiptTextIcon,
  PrinterIcon,
  TrashIcon,
  UploadIcon,
  LogsIcon,
} from '../components/icons'
import { amountInput, formatTRY, kurusToLiraInput, liraToKurus } from '../utils/money'
import { BASE_PORTION, DEFAULT_NOTE_OPTIONS, DEFAULT_STATION, type Category, type Portion, type Product, type Tare } from '../types'
import { ConfirmDialog, type ConfirmSpec } from '../components/pos/ConfirmDialog'
import { PromptDialog, type PromptSpec } from '../components/pos/PromptDialog'
import { StationDialog } from '../components/menu/StationDialog'
import { StationsDialog } from '../components/menu/StationsDialog'
import { NoteOptionsDialog } from '../components/pos/NoteOptionsDialog'
import { TagListEditor } from '../components/pos/TagListEditor'
import { MenuPrintDialog } from '../components/menu/MenuPrintDialog'
import { MenuImportDialog } from '../components/menu/MenuImportDialog'
import './MenuAdminPage.css'
import { Select } from '../components/Select'
import { ImageIcon, LockIcon, ScanBarcodeIcon, XIcon } from '../components/icons'
import { useUpgrade } from '../context/UpgradeContext'
import { planFor } from '../plans'
import { CameraScanner } from '../components/pos/CameraScanner'
import { deleteMenuImage, uploadMenuImage } from '../services/menuImages'
import { formatStock, stockState } from '../utils/stock'
import { useCosts } from '../hooks/useCosts'
import { lineCostKurus, margin, parseQty, qtyInput, qtyToInput, unitCostKurus, UNIT_LABEL } from '../utils/cost'
import { deleteProductCost, saveMaterial, saveProductCost } from '../services/products'
import type { FeatureKey, MaterialUnit, ProductCost, RecipeLine } from '../types'
import { MaterialsDialog } from '../components/menu/MaterialsDialog'
import { normalizeBarcode } from '../utils/barcode'
import { stationNames } from '../utils/stations'
import { categoryColor } from '../utils/categoryColors'
import { CategoryColorPicker } from '../components/menu/CategoryColorPicker'
import { MenuDnd, ProductList, SortableRow, catKey, prodKey, type MenuLayout } from '../components/menu/Sortable'

interface Draft {
  id?: string
  name: string
  categoryId: string
  lira: string
  description: string
  active: boolean
  /**
   * Comma-separated note options, or blank to inherit the category's. Kept as
   * the raw string while editing so a half-typed list isn't parsed away.
   */
  notes: string[]
  /** Whether this item overrides its category at all. */
  notesOverridden: boolean
  /** Tartılı ürünler modülü: kiloyla satılır, fiyat 1 kg'ın. */
  byWeight: boolean
  /** Seçili kap: '' yok, 'new' yeni, ya da "ad|gram". */
  tareKey: string
  tareName: string
  tareGr: string
  /** Görselli menü: şu anki fotoğraf ve pencere açıldığındaki (kayıtlı) hali. */
  imageUrl: string | null
  savedImageUrl: string | null
  /** Barkodlar ve kutuya yazılmakta olan. */
  barcodes: string[]
  barcodeInput: string
  /** Ürüne özel tezgah; '' kategorinin tezgahı. */
  station: string
  /** Porsiyonlu ürün (3 Kas 2026): açık mı, ek porsiyonlar yazıldığı gibi. */
  portionsOn: boolean
  portions: PortionRow[]
  /** Stoklu ürün (soldaki seçenek); kapalıysa sınırsız. */
  tracked: boolean
  savedTracked: boolean
  /** Stok ve az kaldı eşiği, yazıldığı gibi; eşik boşsa uyarı yok. */
  stock: string
  stockMin: string
  /** Pencere açıldığındaki haller: yalnız değiştiyse yazılıyor. */
  savedStock: string
  savedStockMin: string
  /** Maliyet (18 Eki 2026), yalnız patron: açık mı, pencere açıldığında açık mıydı. */
  costOn: boolean
  savedCostOn: boolean
  costMode: 'direct' | 'recipe'
  /** Tek tutar: 1 kg ya da 1 adet maliyeti, yazıldığı gibi. */
  costLira: string
  /** Hammaddeden: satırlar, miktar yazıldığı gibi. */
  recipe: RecipeRow[]
  /** Satırın içinden yeni hammadde ekleniyorsa o satırın anahtarı ve alanlar. */
  newMat: { rowKey: string; name: string; unit: MaterialUnit; lira: string } | null
}

interface PortionRow {
  key: string
  name: string
  lira: string
}

const newPortionRow = (name = '', lira = ''): PortionRow => ({ key: crypto.randomUUID(), name, lira })

interface RecipeRow {
  key: string
  materialId: string
  qty: string
}

const newRow = (): RecipeRow => ({ key: crypto.randomUUID(), materialId: '', qty: '' })

/** 12 → "12", 1.25 → "1,25", null → "". */
const numInput = (n?: number | null) => (n == null ? '' : String(n).replace('.', ','))
/** "" → null, "1,25" → 1.25; bozuksa NaN. */
const parseNum = (s: string) => (s.trim() === '' ? null : Number(s.trim().replace(',', '.')))

const tareKeyOf = (t?: Tare | null) => (t ? `${t.name}|${t.gr}` : '')

/** Tezgah listesinde "+ Yeni tezgah" seçeneğinin değeri; bir tezgah adı olamaz. */
const NEW_STATION = '\u0000yeni'
/** Aynı listede "Tezgahları düzenle"; o da bir tezgah adı olamaz. */
const MANAGE_STATIONS = '\u0000duzenle'

const emptyDraft = (categoryId: string): Draft => ({
  name: '',
  categoryId,
  lira: '',
  description: '',
  active: true,
  notes: [],
  notesOverridden: false,
  byWeight: false,
  tareKey: '',
  tareName: '',
  tareGr: '',
  imageUrl: null,
  savedImageUrl: null,
  barcodes: [],
  barcodeInput: '',
  station: '',
  portionsOn: false,
  portions: [],
  tracked: false,
  savedTracked: false,
  stock: '',
  stockMin: '',
  savedStock: '',
  savedStockMin: '',
  costOn: false,
  savedCostOn: false,
  costMode: 'direct',
  costLira: '',
  recipe: [newRow()],
  newMat: null,
})

/** Kayıtlı maliyeti taslak alanlarına çevirir. */
const costDraft = (c: ProductCost | undefined) => ({
  costOn: c != null,
  savedCostOn: c != null,
  costMode: c?.mode ?? ('direct' as const),
  costLira: c?.costKurus != null ? kurusToLiraInput(c.costKurus) : '',
  recipe: c?.recipe.length
    ? c.recipe.map((l) => ({ key: crypto.randomUUID(), materialId: l.materialId, qty: qtyToInput(l.qty) }))
    : [newRow()],
  newMat: null,
})

export function MenuAdminPage() {
  const { tenantId, tenantName, hasFeature, isOwner, receiptSettings } = useAuth()
  // Maliyet ve hammaddeler yalnız patrona: tabloları başkası okuyamıyor.
  const { materials, materialMap, costMap } = useCosts(tenantId, isOwner)
  const [materialsOpen, setMaterialsOpen] = useState(false)
  /** Ürün penceresinde sağda gösterilen özellik sekmesi. */
  const [tab, setTab] = useState<OptTab | null>(null)
  /** Tik açılınca sekmesini göster; kapanınca o sekme açıksa kapat. */
  const showTab = (next: OptTab | null, own: OptTab) => setTab((t) => (next ? next : t === own ? null : t))
  const weighOn = hasFeature('tarti')
  const photoOn = hasFeature('gorsel')
  // Paketle gelenler (29 Eki 2026, plans.ts): Plus'ta stok, Max'ta maliyet.
  const stockOn = hasFeature('stok')
  const costOn = hasFeature('maliyet')
  const askUpgrade = useUpgrade()
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const { categories, products, loading: catalogLoading, error } = useCatalog()
  const skeletonDemo = useSkeletonDemo()
  const loading = catalogLoading || skeletonDemo

  // Sürüklenip bırakılan, henüz sunucudan dönmemiş sıra (onReorder*).
  const [catOrder, setCatOrder] = useState<string[] | null>(null)
  const [pendingLayout, setPendingLayout] = useState<MenuLayout | null>(null)
  useEffect(() => {
    if (catOrder && categories.map((c) => c.id).join() === catOrder.join()) setCatOrder(null)
  }, [categories, catOrder])
  const inOrder = <T extends { id: string }>(list: T[], ids?: string[] | null): T[] =>
    ids ? [...list].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)) : list
  const shownCategories = inOrder(categories, catOrder)
  // Kategori -> ürün kimlikleri, sunucudaki sırayla.
  const baseLayout: MenuLayout = Object.fromEntries(
    categories.map((c) => [c.id, products.filter((p) => p.categoryId === c.id).map((p) => p.id)]),
  )
  const baseKey = JSON.stringify(baseLayout)
  useEffect(() => {
    if (!pendingLayout) return
    const same = Object.keys(pendingLayout).every(
      (k) => (pendingLayout[k] ?? []).join() === JSON.parse(baseKey)[k]?.join(),
    )
    if (same) setPendingLayout(null)
  }, [baseKey, pendingLayout])
  const shownLayout = pendingLayout ?? baseLayout
  const productById = new Map(products.map((p) => [p.id, p]))
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  /** The question on screen, if any, see the same pattern on the till. */
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)
  /** The one-line text prompt on screen, if any. */
  const [prompt, setPrompt] = useState<PromptSpec | null>(null)
  const [stationFor, setStationFor] = useState<Category | null>(null)
  const [stationsOpen, setStationsOpen] = useState(false)
  /** Fişi basılmayan tezgahlar (yalnız mutfak ekranı). */
  const skipped = receiptSettings.skipStations ?? []
  /** The category whose note options are open for editing. */
  const [noteEdit, setNoteEdit] = useState<Category | null>(null)
  /** Whether the printable-menu builder is open. */
  const [printing, setPrinting] = useState(false)
  const [importing, setImporting] = useState(false)

  /** Shorthand for the failures that all read the same. */
  const failed = (what: string) =>
    setAsk({ title: what, body: 'İnternet bağlantınızı kontrol edin.', confirmLabel: 'Tamam' })

  if (loading) return <Skeleton kind="grid" label="Menü yükleniyor" />
  if (error) return <div className="menu-page"><p className="menu-error">{error}</p></div>

  /** Diğer ürünlerde tanımlı kaplar: yeni ürüne açılır listeden eklensin. */
  const knownTares: Tare[] = []
  for (const p of products) {
    if (p.tare && !knownTares.some((t) => tareKeyOf(t) === tareKeyOf(p.tare))) knownTares.push(p.tare)
  }
  knownTares.sort((a, b) => a.name.localeCompare(b.name, 'tr'))

  /**
   * Ürüne özel tezgah (20 Eki 2026). Kategorilerin ve ürünlerin tezgahları;
   * 21 Eki'den beri ürüne listede olmayan yeni bir tezgah da yazılabiliyor
   * (kullanıcı), o ürün fişte kendi adıyla ayrı basılıyor. Yazıcı ve mutfak
   * ekranı ayarları da aynı listeyi (stationNames) kullanıyor.
   */
  const knownStations = stationNames(categories, products)
  const categoryStation = (categoryId: string) =>
    categories.find((c) => c.id === categoryId)?.station || DEFAULT_STATION
  /** Kategorisinden farklıysa ürünün tezgahı, değilse null. */
  const ownStation = (p: { station?: string | null; categoryId: string }) =>
    p.station && p.station !== categoryStation(p.categoryId) ? p.station : null

  /** Barkodlardan biri başka bir üründe kayıtlıysa onu söyler. */
  const barcodeClash = (codes: string[], selfId?: string) => {
    for (const code of codes) {
      const other = products.find(
        (p) => p.id !== selfId && (p.barcodes ?? []).some((b) => normalizeBarcode(b) === code),
      )
      if (other) return { code, name: other.name }
    }
    return null
  }

  /**
   * Kamerayla okutup ürüne barkod ekleme (29 Eki 2026, kullanıcı): telefonda
   * USB okuyucu yok, kutuya dokununca klavye açılıp barkodu elle yazmak
   * gerekiyordu. Okutulan kod kutucuk olarak eklenir, pencere kapanır.
   */
  const [scanningBarcode, setScanningBarcode] = useState(false)
  const scanBarcodeToDraft = (raw: string): { text: string; ok: boolean } => {
    const code = normalizeBarcode(raw)
    if (!code) return { text: 'Barkod okunamadı', ok: false }
    const clash = barcodeClash([code], draft?.id)
    if (clash) return { text: `${clash.code} "${clash.name}" ürününde kayıtlı`, ok: false }
    setDraft((d) => (!d || d.barcodes.includes(code) ? d : { ...d, barcodes: [...d.barcodes, code] }))
    window.setTimeout(() => setScanningBarcode(false), 700)
    return { text: `${code} eklendi`, ok: true }
  }

  /** Kutudaki barkodu ekler (okuyucunun Enter'ı ya da elle Enter). */
  const addBarcode = () => {
    if (!draft) return
    const code = normalizeBarcode(draft.barcodeInput)
    if (!code) return
    const clash = barcodeClash([code], draft.id)
    if (clash) {
      setFormError(`${clash.code} barkodu "${clash.name}" ürününde kayıtlı.`)
      setDraft({ ...draft, barcodeInput: '' })
      return
    }
    setFormError(null)
    setDraft({
      ...draft,
      barcodeInput: '',
      barcodes: draft.barcodes.includes(code) ? draft.barcodes : [...draft.barcodes, code],
    })
  }

  const openNew = (categoryId: string) => {
    setFormError(null)
    setTab(null)
    setDraft(emptyDraft(categoryId))
  }

  const openEdit = (p: Product) => {
    setFormError(null)
    // İlk açık özelliğin sekmesiyle açılıyor; hiçbiri açık değilse yalnız bilgiler.
    const c = costMap.get(p.id)
    setTab(
      weighOn && p.byWeight
        ? 'weigh'
        : p.portions?.length
          ? 'portions'
          : p.stock != null
          ? 'stock'
          : isOwner && c
            ? 'cost'
            : p.noteOptions != null
              ? 'notes'
              : null,
    )
    setDraft({
      id: p.id,
      name: p.name,
      categoryId: p.categoryId,
      lira: kurusToLiraInput(p.priceKurus),
      description: p.description ?? '',
      active: p.active,
      notes: p.noteOptions ?? [],
      notesOverridden: p.noteOptions != null,
      byWeight: p.byWeight === true,
      tareKey: tareKeyOf(p.tare),
      tareName: '',
      tareGr: '',
      imageUrl: p.imageUrl ?? null,
      savedImageUrl: p.imageUrl ?? null,
      barcodes: p.barcodes ?? [],
      barcodeInput: '',
      station: p.station ?? '',
      portionsOn: !!p.portions?.length,
      portions: (p.portions ?? []).map((x) => newPortionRow(x.name, kurusToLiraInput(x.priceKurus))),
      tracked: p.stock != null,
      savedTracked: p.stock != null,
      stock: numInput(p.stock),
      stockMin: numInput(p.stockMin),
      savedStock: numInput(p.stock),
      savedStockMin: numInput(p.stockMin),
      ...costDraft(costMap.get(p.id)),
    })
  }

  /** Seçilen fotoğrafı küçültüp hemen yükler; ürüne Kaydet'te bağlanır. */
  const onPickImage = async (file: File | undefined) => {
    if (!file || !draft || !tenantId) return
    setUploading(true)
    setFormError(null)
    try {
      const url = await uploadMenuImage(tenantId, draft.id ?? 'yeni', file)
      // Bu pencerede az önce yüklenip vazgeçilen fotoğraf kovada kalmasın.
      if (draft.imageUrl && draft.imageUrl !== draft.savedImageUrl) void deleteMenuImage(draft.imageUrl)
      setDraft((d) => (d ? { ...d, imageUrl: url } : d))
    } catch (err) {
      console.error('[menu] image', err)
      setFormError(err instanceof Error && err.message.includes('açılamadı') ? err.message : 'Fotoğraf yüklenemedi. İnternet bağlantınızı kontrol edin.')
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  /** Maliyet satırları: tek satırı değiştir ya da sil (en az bir satır kalır). */
  const updateRow = (key: string, patch: Partial<RecipeRow>) =>
    setDraft((d) => d && { ...d, recipe: d.recipe.map((r) => (r.key === key ? { ...r, ...patch } : r)) })
  const removeRow = (key: string) =>
    setDraft((d) => {
      if (!d) return d
      const rest = d.recipe.filter((r) => r.key !== key)
      return { ...d, recipe: rest.length ? rest : [newRow()], newMat: d.newMat?.rowKey === key ? null : d.newMat }
    })

  /** Satırın içinden yeni hammadde: kaydedip o satırda seçili yapar. */
  const onAddMaterial = async () => {
    const nm = draft?.newMat
    if (!nm || !tenantId) return
    const priceKurus = liraToKurus(nm.lira)
    if (!nm.name.trim() || priceKurus === null || priceKurus < 0) {
      setFormError(`Hammaddenin adını ve 1 ${UNIT_LABEL[nm.unit]} fiyatını yazın.`)
      return
    }
    setFormError(null)
    try {
      const id = await saveMaterial(tenantId, { name: nm.name.trim(), unit: nm.unit, priceKurus })
      updateRow(nm.rowKey, { materialId: id })
      setDraft((d) => d && { ...d, newMat: null })
    } catch (err) {
      console.error('[maliyet] hammadde', err)
      setFormError('Hammadde eklenemedi. İnternet bağlantınızı kontrol edin.')
    }
  }

  /** Pencere kapanırken: kaydedilmeyen yeni fotoğrafı sil. */
  const closeDraft = () => {
    if (draft?.imageUrl && draft.imageUrl !== draft.savedImageUrl) void deleteMenuImage(draft.imageUrl)
    setDraft(null)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!draft || !tenantId) return

    const priceKurus = liraToKurus(draft.lira)
    if (priceKurus === null) {
      setFormError('Geçerli bir fiyat girin.')
      return
    }
    if (!draft.name.trim()) {
      setFormError('Ürün adı boş olamaz.')
      return
    }
    // Kap: listeden seçilen, yeni yazılan ya da yok.
    let tare: Tare | null = null
    if (draft.byWeight && draft.tareKey === 'new') {
      const gr = Number(draft.tareGr.replace(',', '.'))
      if (!draft.tareName.trim() || !Number.isFinite(gr) || gr < 0 || gr > 20000) {
        setFormError('Kap adını ve gramajını girin (örn. Porselen tabak, 280).')
        setTab('weigh')
        return
      }
      tare = { name: draft.tareName.trim(), gr: Math.round(gr) }
    } else if (draft.byWeight && draft.tareKey) {
      tare = knownTares.find((t) => tareKeyOf(t) === draft.tareKey) ?? null
    }

    // Porsiyonlar (3 Kas 2026): boş satırlar atlanır; yazılanın adı ve fiyatı tam olmalı.
    let portions: Portion[] | null = null
    if (draft.portionsOn && !draft.byWeight) {
      const rows = draft.portions.filter((r) => r.name.trim() || r.lira.trim())
      const seen = new Set([BASE_PORTION.toLocaleLowerCase('tr')])
      const list: Portion[] = []
      for (const r of rows) {
        const name = r.name.trim().replace(/\s+/g, ' ')
        const kurus = liraToKurus(r.lira)
        const bad = !name
          ? 'Porsiyonun adını yazın (örn. 1,5 Porsiyon).'
          : seen.has(name.toLocaleLowerCase('tr'))
            ? `"${name}" iki kez yazılmış.`
            : kurus === null || !(kurus > 0)
              ? `"${name}" için fiyat yazın.`
              : null
        if (bad) {
          setFormError(bad)
          setTab('portions')
          return
        }
        seen.add(name.toLocaleLowerCase('tr'))
        list.push({ name, priceKurus: kurus! })
      }
      portions = list.length ? list : null
    }
    const stock = draft.tracked ? parseNum(draft.stock) : null
    const stockMin = draft.tracked ? parseNum(draft.stockMin) : null
    if (draft.tracked && (stock === null || !Number.isFinite(stock) || stock < 0)) {
      setFormError(draft.byWeight ? 'Stoğu kg olarak yazın (örn. 5 ya da 2,5).' : 'Stokta kaç adet olduğunu yazın.')
      setTab('stock')
      return
    }
    if (stockMin !== null && (!Number.isFinite(stockMin) || stockMin < 0)) {
      setFormError('Az kaldı uyarısını rakamla yazın ya da boş bırakın.')
      setTab('stock')
      return
    }
    // Maliyet (yalnız patron): tek tutar ya da hammadde satırları.
    let cost: ProductCost | null = null
    if (isOwner && draft.costOn) {
      if (draft.costMode === 'direct') {
        const costKurus = liraToKurus(draft.costLira)
        if (costKurus === null || costKurus < 0) {
          setFormError(draft.byWeight ? '1 kg maliyetini yazın (örn. 148).' : '1 adet maliyetini yazın (örn. 85).')
          setTab('cost')
          return
        }
        cost = { productId: '', mode: 'direct', costKurus, recipe: [] }
      } else {
        // Tamamen boş satırlar sayılmıyor; yarım satır hata.
        const rows = draft.recipe.filter((r) => r.materialId || r.qty.trim())
        const recipe: RecipeLine[] = []
        for (const r of rows) {
          const qty = parseQty(r.qty)
          if (!r.materialId || qty === null || qty <= 0) {
            setFormError('Her hammadde satırında hammaddeyi seçin ve miktarını yazın.')
          setTab('cost')
            return
          }
          recipe.push({ materialId: r.materialId, qty })
        }
        if (recipe.length === 0) {
          setFormError('En az bir hammadde ekleyin ya da "Tek tutar"ı seçin.')
          setTab('cost')
          return
        }
        cost = { productId: '', mode: 'recipe', costKurus: null, recipe }
      }
    }

    // Kutuda Enter'a basılmadan kalan barkod da sayılıyor.
    const pending = normalizeBarcode(draft.barcodeInput)
    const barcodes = pending && !draft.barcodes.includes(pending) ? [...draft.barcodes, pending] : draft.barcodes
    const clash = barcodeClash(barcodes, draft.id)
    if (clash) {
      setFormError(`${clash.code} barkodu "${clash.name}" ürününde kayıtlı.`)
      return
    }

    // Stok yalnız değiştiyse yazılıyor: pencere açıkken yapılan satışlar ezilmesin.
    // Stoklu ürün kapatıldıysa ikisi de siliniyor (sınırsız).
    const stockChanged = draft.tracked !== draft.savedTracked || (draft.tracked && draft.stock !== draft.savedStock)
    const minChanged = draft.tracked !== draft.savedTracked || (draft.tracked && draft.stockMin !== draft.savedStockMin)

    setBusy(true)
    setFormError(null)
    try {
      const siblings = products.filter((p) => p.categoryId === draft.categoryId)
      const sortOrder =
        products.find((p) => p.id === draft.id)?.sortOrder ??
        (siblings.length ? Math.max(...siblings.map((p) => p.sortOrder)) + 10 : 10)

      const productId = await saveProduct(tenantId, {
        id: draft.id,
        name: draft.name.trim(),
        categoryId: draft.categoryId,
        priceKurus,
        description: draft.description.trim() || null,
        active: draft.active,
        sortOrder,
        // null hands the decision back to the category; an array, including an
        // empty one, is this item saying it knows better.
        noteOptions: draft.notesOverridden ? draft.notes : null,
        byWeight: draft.byWeight,
        tare,
        // Modül kapalıyken hiç gönderilmiyor: kayıtlı fotoğraf olduğu gibi kalır.
        ...(photoOn ? { imageUrl: draft.imageUrl } : {}),
        barcodes,
        // Kategorininkiyle aynıysa boş: kategori sonradan taşınırsa ürün de gitsin.
        station: ownStation(draft),
        portions,
        ...(stockChanged ? { stock } : {}),
        ...(minChanged ? { stockMin } : {}),
      })
      if (cost) await saveProductCost(tenantId, { ...cost, productId })
      else if (isOwner && draft.savedCostOn && draft.id) await deleteProductCost(tenantId, draft.id)
      // Değiştirilen ya da kaldırılan eski fotoğraf artık kullanılmıyor.
      if (photoOn && draft.savedImageUrl && draft.savedImageUrl !== draft.imageUrl) {
        void deleteMenuImage(draft.savedImageUrl)
      }
      setDraft(null)
    } catch (err) {
      console.error(err)
      setFormError('Kaydedilemedi. İnternet bağlantınızı kontrol edin.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Toggles whether the item shows on the till. No confirmation: this gets
   * flipped repeatedly as trays run out and get refilled, and the change is
   * both visible and instantly reversible.
   */
  const onToggleActive = async (p: Product) => {
    if (!tenantId) return
    try {
      await setProductActive(tenantId, p.id, !p.active)
    } catch (err) {
      console.error('[menu] toggle', err)
      failed('Değiştirilemedi')
    }
  }


  /**
   * Permanent removal. Safe for history: order lines carry their own copy of
   * the name and price, so past receipts and reports are unaffected. The
   * wording steers stock problems to the eye toggle instead.
   */
  const onDelete = async (p: Product) => {
    if (!tenantId) return
    setAsk({
      title: `"${p.name}" silinsin mi?`,
      body: 'Geçmiş satışlar ve raporlar etkilenmez. Ürün sadece bittiyse silmek yerine göz simgesiyle gizleyin.',
      confirmLabel: 'Menüden sil',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteProduct(tenantId, p.id)
        } catch (err) {
          console.error('[menu] delete', err)
          failed('Silinemedi')
        }
      },
    })
  }

  const onAddCategory = async () => {
    if (!tenantId) return
    setPrompt({
      title: 'Yeni kategori',
      label: 'Kategori adı',
      placeholder: 'Örn. İçecekler',
      confirmLabel: 'Ekle',
      onConfirm: async (name) => {
        const sortOrder = categories.length
          ? Math.max(...categories.map((c) => c.sortOrder)) + 10
          : 10
        await saveCategory(tenantId, { name, sortOrder, active: true })
      },
    })
  }

  /**
   * Sürükle-bırak sırası (25 Eki 2026). Yeni sıra yazılırken liste hemen
   * yeni haliyle görünüyor (catOrder / prodOrder); sunucudan aynı sıra
   * gelince bu geçici sıra bırakılıyor. Yazılamazsa eski sıraya dönüyor.
   */
  const onReorderCategories = async (ids: string[]) => {
    if (!tenantId) return
    setCatOrder(ids)
    try {
      await reorderCategories(
        tenantId,
        ids.map((id) => categories.find((c) => c.id === id)).filter((c): c is Category => Boolean(c)),
      )
    } catch (err) {
      console.error('[menu] order', err)
      setCatOrder(null)
      failed('Sıra değiştirilemedi')
    }
  }

  /** Ürünlerin yeni yerleşimi: sıra ve gerekirse kategori. */
  const onMoveProducts = async (next: MenuLayout) => {
    if (!tenantId) return
    setPendingLayout(next)
    const moves = Object.entries(next).flatMap(([categoryId, ids]) =>
      ids.flatMap((id, sortOrder) => {
        const product = productById.get(id)
        return product ? [{ product, categoryId, sortOrder }] : []
      }),
    )
    try {
      await moveProducts(tenantId, moves)
    } catch (err) {
      console.error('[menu] product move', err)
      setPendingLayout(null)
      failed('Ürün taşınamadı')
    }
  }

  /**
   * Removes an empty category.
   *
   * Refused while it still holds items: deleting it would leave them with a
   * categoryId nothing matches, so they would vanish from both the menu screen
   * and the till while still existing in the database.
   */
  const onDeleteCategory = async (category: Category) => {
    if (!tenantId) return
    const items = products.filter((p) => p.categoryId === category.id)
    if (items.length > 0) {
      setAsk({
        title: `"${category.name}" boş değil`,
        body: `İçinde ${items.length} ürün var. Önce ürünleri silin ya da başka bir kategoriye taşıyın.`,
        confirmLabel: 'Tamam',
      })
      return
    }
    setAsk({
      title: `"${category.name}" kategorisi silinsin mi?`,
      body: 'Kategori boş, geçmiş satışlar etkilenmez.',
      confirmLabel: 'Kategoriyi sil',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteCategory(tenantId, category.id)
        } catch (err) {
          console.error('[menu] delete category', err)
          failed('Silinemedi')
        }
      },
    })
  }

  const onChangeStation = (category: Category) => {
    if (tenantId) setStationFor(category)
  }

  /** Tezgah penceresi: kategorinin tezgahı ve o tezgahın fişi basılsın mı. */
  const saveStation = async (category: Category, station: string, print: boolean) => {
    if (!tenantId) return
    try {
      if (station !== (category.station || DEFAULT_STATION)) await saveCategory(tenantId, { ...category, station })
      if (print === skipped.includes(station)) {
        const skipStations = print ? skipped.filter((s) => s !== station) : [...skipped, station]
        await saveTenantSettings(tenantId, { receipt: { ...receiptSettings, skipStations } })
      }
    } catch (err) {
      console.error('[menu] station', err)
      failed('Kaydedilemedi')
    }
  }

  /** Tezgahları düzenle penceresinin satırları. */
  const stationUses = () => {
    // Açık ürün penceresinde seçilip henüz kaydedilmemiş yeni tezgah da listede.
    const unsaved = draft?.station && !knownStations.includes(draft.station) ? draft.station : null
    return [...knownStations, ...(unsaved ? [unsaved] : [])].map((name) => {
      const cats = categories.filter((c) => (c.station || DEFAULT_STATION) === name)
      // Bu tezgahın fişine düşen ürünler: kendi tezgahı bu olanlar ve
      // kategorisi bu tezgahta olup başka tezgaha verilmemiş olanlar.
      const goes = products.filter((p) => (p.station || categoryStation(p.categoryId)) === name)
      return {
        name,
        categoryNames: cats.map((c) => c.name),
        products: goes.length,
        unsaved: name === unsaved,
        noPrint: skipped.includes(name),
      }
    })
  }

  /** Ürüne özel tezgahı sil: ürünler kategorilerinin tezgahına döner. */
  const onDeleteStation = (name: string) => {
    if (!tenantId) return
    const n = products.filter((p) => p.station === name).length
    const cats = categories.filter((c) => (c.station || DEFAULT_STATION) === name)
    // Yalnız açık pencerede seçilmiş, kaydedilmemiş: kaydetmeden geri al.
    if (n === 0 && cats.length === 0) {
      setDraft((d) => (d && d.station === name ? { ...d, station: '' } : d))
      return
    }
    // Kategorideki tezgah da siliniyor (kullanıcı, 25 Eki 2026): o kategoriler
    // varsayılan tezgaha (Mutfak) geçiyor.
    const body = [
      cats.length > 0 && `${cats.map((c) => c.name).join(', ')} "${DEFAULT_STATION}" tezgahına geçer.`,
      n > 0 && `${n} ürün kendi kategorisinin tezgahına döner.`,
      'Ürünler silinmez.',
    ]
      .filter(Boolean)
      .join(' ')
    setAsk({
      title: `"${name}" tezgahı silinsin mi?`,
      body,
      confirmLabel: 'Tezgahı sil',
      danger: true,
      onConfirm: async () => {
        try {
          for (const c of cats) await saveCategory(tenantId, { ...c, station: DEFAULT_STATION })
          if (n > 0) await clearProductStation(tenantId, name)
          if (skipped.includes(name)) {
            await saveTenantSettings(tenantId, {
              receipt: { ...receiptSettings, skipStations: skipped.filter((s) => s !== name) },
            })
          }
          setDraft((d) => (d && d.station === name ? { ...d, station: '' } : d))
        } catch (err) {
          console.error('[menu] station delete', err)
          failed('Silinemedi')
        }
      },
    })
  }

  /** Kategori rengi (22 Eki 2026); null otomatik, sıraya göre. */
  const onChangeColor = async (category: Category, color: string | null) => {
    if (!tenantId) return
    try {
      await saveCategory(tenantId, { ...category, color })
    } catch (err) {
      console.error('[menu] color', err)
      failed('Renk kaydedilemedi')
    }
  }

  /**
   * The note options for a category, as a comma-separated list. Free text
   * rather than a fixed set: what a customer asks to leave out is the yurt's
   * own vocabulary, and "Buzsuz" belongs to drinks the way "Soğansız" belongs
   * to a dürüm. Left blank means the items take no tick-box notes at all.
   */
  const onChangeNotes = (category: Category) => {
    if (!tenantId) return
    setNoteEdit(category)
  }

  const onSaveNotes = async (category: Category, noteOptions: string[]) => {
    if (!tenantId) return
    setNoteEdit(null)
    try {
      await saveCategory(tenantId, { ...category, noteOptions })
    } catch (err) {
      console.error('[menu] notes', err)
      failed('Kaydedilemedi')
    }
  }

  const onRenameCategory = async (id: string, current: string) => {
    if (!tenantId) return
    setPrompt({
      title: 'Kategori adı',
      label: 'Ad',
      initial: current,
      onConfirm: async (name) => {
        if (name === current) return
        const category = categories.find((c) => c.id === id)
        if (!category) return
        await saveCategory(tenantId, { ...category, name })
      },
    })
  }

  return (
    <div className="menu-page">
      <div className="menu-head">
        <h1>Menü Yönetimi</h1>
        <div className="menu-head__actions">
          {/* İkon ve etiket arasındaki çizgi, kasadaki ek ödeme düğmelerindeki
              ile aynı: ikon ne yapacağını, etiket neyi yapacağını söylüyor ve
              ikisi iki ayrı bilgi. Aynı desen iki ekranda da geçerli. */}
          <button type="button" className="btn btn--ghost" onClick={() => setPrinting(true)}>
            <PrinterIcon />
            <i className="btn__rule" />
            {/* Telefonda kısa: "Yazdır", "İçe Aktar" (CSS menu-head__long). */}
            <span className="menu-head__long">Menü </span>Yazdır
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setImporting(true)}>
            <UploadIcon />
            <i className="btn__rule" />
            <span className="menu-head__long">Excel'den </span>İçe Aktar
          </button>
          {/* Diğer ikisiyle aynı düzen: artı ikonu, ayraç, etiket. */}
          <button type="button" className="btn btn--ghost" onClick={onAddCategory}>
            <PlusIcon />
            <i className="btn__rule" />
            Kategori
          </button>
        </div>
      </div>

      {/* Hiç kategori yokken sayfa bomboş açılıyordu: ekranda yalnızca üç düğme
          duruyor ve hangisine basılacağını, basınca ne olacağını söyleyen bir
          şey yok. Kurulumunu yeni bitirmiş biri tam buraya düşüyor.

          Sıra anlatılıyor çünkü sıra zorunlu: ürün bir kategorinin içine
          giriyor, yani kategori olmadan menüye tek bir şey bile eklenemiyor.
          Bunu denemeden öğrenmenin yolu yoktu. */}
      {categories.length === 0 && (
        <section className="menu-start">
          <h2 className="menu-start__title">Menünüz henüz boş</h2>
          <p className="menu-start__lead">
            Ürünler kategorilerin içine giriyor, o yüzden önce bir kategori
            açmanız gerekiyor. Sonrası birkaç dakika.
          </p>

          <ol className="menu-start__steps">
            <li>
              <b>Kategori açın.</b> Menünüzün bölümleri: Çorbalar, Ana Yemekler,
              İçecekler… Sıralarını sonradan değiştirebilirsiniz; kasada en
              üstteki kategori ilk görünür.
            </li>
            <li>
              <b>Ürünleri ekleyin.</b> Her ürünün adı ve fiyatı yeterli.
              Kalabalık bir menüyü tek tek yazmak yerine{' '}
              <b>Excel'den İçe Aktar</b> ile toplu yükleyebilirsiniz.
            </li>
            <li>
              <b>Tezgahları ayırın.</b> Mutfak ve bar ayrı fiş alacaksa her
              kategoriye tezgahını yazın. Tek tezgahlı bir yerde buna hiç
              dokunmanız gerekmez.
            </li>
          </ol>

          <button type="button" className="btn btn--primary" onClick={onAddCategory}>
            İlk kategoriyi açın
          </button>
        </section>
      )}

      <MenuDnd
        categoryIds={shownCategories.map((c) => c.id)}
        layout={shownLayout}
        onCategories={(ids) => void onReorderCategories(ids)}
        onProducts={(next) => void onMoveProducts(next)}
      >
      {(live) => shownCategories.map((category) => {
        // Every item, hidden ones included: a hidden item has to be findable to
        // be brought back, and the eye beside each row already says which is
        // which.
        const items = (live[category.id] ?? [])
          .map((id) => productById.get(id))
          .filter((p): p is Product => Boolean(p))

        return (
          <SortableRow key={category.id} id={catKey(category.id)}>
          {(drag) => (
          <section
            ref={drag.setNodeRef}
            className={`menu-cat ${drag.dragging ? 'is-dragging' : ''}`}
            style={{ ...drag.style, '--cat': categoryColor(category, categories).hex } as CSSProperties}
          >
            <header className="menu-cat__head">
              {/* Order matters at the till: the first category is the one under
                  the cashier's hand. */}
              <button
                type="button"
                className="menu-drag"
                title="Sürükleyip taşı"
                aria-label={`${category.name} kategorisini taşı`}
                {...drag.handle}
              >
                <GripVerticalIcon />
              </button>
              {/* Kategorinin rengi: kasa ve garsonda ürün kartları bu tonda. */}
              <CategoryColorPicker
                hex={categoryColor(category, categories).hex}
                chosen={category.color}
                categoryName={category.name}
                onPick={(c) => onChangeColor(category, c)}
              />
              <button
                type="button"
                className="menu-cat__name"
                onClick={() => onRenameCategory(category.id, category.name)}
                title="Adını değiştir"
              >
                {category.name}
              </button>
              <div className="menu-cat__actions">
                {/* Which counter this category's items are prepared at; the
                    receipt splits into one slip per station. */}
                <button
                  type="button"
                  className={`menu-cat__station ${skipped.includes(category.station || DEFAULT_STATION) ? 'is-noprint' : ''}`}
                  onClick={() => onChangeStation(category)}
                  title={
                    skipped.includes(category.station || DEFAULT_STATION)
                      ? 'Tezgah: fiş basılmıyor, yalnız mutfak ekranı'
                      : 'Tezgahı değiştir. Fiş tezgaha göre ayrı basılır'
                  }
                  aria-label={`Tezgah: ${category.station || DEFAULT_STATION}`}
                >
                  <ReceiptTextIcon />
                  <i className="btn__rule" />
                  <span className="menu-cat__label">{category.station || DEFAULT_STATION}</span>
                </button>
                {/* Which note options this section's items offer. Asking whether
                    a bottle of water wants onion is nonsense, so it is set here
                    and inherited by every product in the category. */}
                <button
                  type="button"
                  className="menu-cat__station"
                  onClick={() => onChangeNotes(category)}
                  title="Bu kategoride sipariş notu olarak neler seçilebilir"
                  aria-label="Sipariş notları"
                >
                  <NotebookPenIcon />
                  <i className="btn__rule" />
                  {(() => {
                    const n = (category.noteOptions ?? DEFAULT_NOTE_OPTIONS).length
                    return (
                      <>
                        <span className="menu-cat__label">{n === 0 ? 'Not ekle' : `${n} not`}</span>
                        {/* Telefonda yazı yok, sayı simgenin köşesinde. */}
                        {n > 0 && <b className="menu-cat__count">{n}</b>}
                      </>
                    )
                  })()}
                </button>
                <button
                  type="button"
                  className="icon-btn is-danger"
                  onClick={() => onDeleteCategory(category)}
                  title="Kategoriyi sil"
                  aria-label={`${category.name} kategorisini sil`}
                >
                  <TrashIcon />
                </button>
                <button
                  type="button"
                  className="btn btn--ghost menu-cat__add"
                  onClick={() => openNew(category.id)}
                  aria-label={`${category.name} kategorisine ürün ekle`}
                >
                  <PlusIcon />
                  <i className="btn__rule" />
                  <span className="menu-cat__label">Ürün</span>
                </button>
              </div>
            </header>

            <ProductList categoryId={category.id} ids={items.map((p) => p.id)}>
            {items.length === 0 ? (
              <p className="menu-cat__empty">Bu kategoride ürün yok. Başka kategoriden ürün sürükleyip bırakabilirsiniz.</p>
            ) : (
              <div className="menu-table">
                <div>
                  {items.map((p) => (
                    <SortableRow key={p.id} id={prodKey(p.id)}>
                    {(row) => (
                    <div
                      ref={row.setNodeRef}
                      style={row.style}
                      className={`menu-row ${p.active ? '' : 'is-inactive'} ${row.dragging ? 'is-dragging' : ''}`}
                    >
                      {/* Stock runs out mid-service and comes back when the
                          next tray arrives, so this has to toggle both ways
                          and sit where the eye lands first. */}
                      {/* Order matters at the till, so it is set where the item
                          is: two small arrows beside the stock toggle. */}
                      <button
                        type="button"
                        className="menu-drag menu-row__move"
                        title="Sürükleyip taşı"
                        aria-label={`${p.name} ürününü taşı`}
                        {...row.handle}
                      >
                        <GripVerticalIcon />
                      </button>
                      <div className="menu-table__toggle">
                        <button
                          type="button"
                          className={`stock ${p.active ? 'is-on' : ''}`}
                          onClick={() => onToggleActive(p)}
                          title={p.active ? 'Kasada görünüyor, gizle' : 'Gizli, kasada göster'}
                          aria-pressed={p.active}
                        >
                          {p.active ? <EyeIcon /> : <EyeOffIcon />}
                        </button>
                      </div>
                      <div className="menu-table__name">
                        {photoOn && p.imageUrl && (
                          <img className="menu-table__img" src={p.imageUrl} alt="" loading="lazy" />
                        )}
                        {p.name}
                        {/* Two different states, and they used to share the
                            word "bitti": hidden is off the menu entirely,
                            sold out is on the grid but flagged at the till. */}
                        {!p.active && <span className="menu-tag">gizli</span>}
                        {p.soldOut && p.active && (
                          <span className="menu-tag menu-tag--out">bitti</span>
                        )}
                        {/* Kategorisinden başka tezgaha giden ürün. */}
                        {ownStation(p) && (
                          <span className="menu-tag menu-tag--station" title="Bu ürünün fişi bu tezgaha gider">
                            <ReceiptTextIcon />
                            {ownStation(p)}
                          </span>
                        )}
                        {/* Stok tutulan üründe kalan; az kaldıysa turuncu. */}
                        {stockState(p) !== 'none' && (
                          <span className={`menu-tag menu-tag--stock is-${stockState(p)}`}>
                            Stok {formatStock(p)}
                            {stockState(p) === 'low' && ' (az kaldı)'}
                          </span>
                        )}
                        {/* Maliyeti girilmiş üründe kar oranı, yalnız patrona. */}
                        {isOwner && (() => {
                          const c = unitCostKurus(costMap.get(p.id), materialMap)
                          if (c === null) return null
                          const { pct } = margin(p.priceKurus, c)
                          return pct === null ? null : (
                            <span
                              className={`menu-tag menu-tag--profit ${pct < 0 ? 'is-neg' : ''}`}
                              title={`Maliyet ${formatTRY(c)}${p.byWeight ? ' / kg' : ''}`}
                            >
                              Kar %{Math.round(pct)}
                            </span>
                          )
                        })()}
                        {p.description && (
                          <span className="menu-table__desc">{p.description}</span>
                        )}
                      </div>
                      <div className="menu-table__price">
                        {formatTRY(p.priceKurus)}
                        {p.byWeight && <small> / kg</small>}
                      </div>
                      <div className="menu-table__actions">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Düzenle"
                          aria-label="Düzenle"
                          onClick={() => openEdit(p)}
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          className="icon-btn is-danger"
                          title="Sil"
                          aria-label="Sil"
                          onClick={() => onDelete(p)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                    )}
                    </SortableRow>
                  ))}
                </div>
              </div>
            )}
            </ProductList>
          </section>
          )}
          </SortableRow>
        )
      })}
      </MenuDnd>

      {draft && (
        <div className="menu-modal" role="dialog" aria-modal="true" aria-label={draft.id ? 'Ürünü düzenle' : 'Yeni ürün'}>
          {/* İki sütun (kullanıcı, 12 Eki 2026): solda fotoğraf ve seçenek
              kutuları, sağda bilgiler. Bir kutu işaretlenince ilgili bölüm
              (tartı, sipariş notları) sağda açılıyor. */}
          <form className="menu-modal__card menu-modal__card--split" onSubmit={onSubmit}>
            <header className="menu-modal__head">
              <h2>{draft.id ? 'Ürünü Düzenle' : 'Yeni Ürün'}</h2>
              <div className="menu-modal__tools">
                {/* Ürüne özel tezgah (kullanıcı, 20 Eki 2026: "çarpının soluna").
                    Her zaman görünüyor: tek tezgahlı işletme de buradan yeni
                    tezgah açabiliyor (21 Eki 2026). */}
                {(() => {
                  const fromCat = categoryStation(draft.categoryId)
                  const others = knownStations.filter((s) => s !== fromCat)
                  if (draft.station && draft.station !== fromCat && !others.includes(draft.station)) {
                    others.push(draft.station)
                  }
                  return (
                    <div className={`pstation ${ownStation(draft) ? 'is-own' : ''}`} title="Bu ürünün fişi hangi tezgaha gitsin">
                      <ReceiptTextIcon />
                      <Select
                        className="pstation__sel"
                        aria-label="Tezgah"
                        value={ownStation(draft) ?? ''}
                        onChange={(v) => {
                          if (v === MANAGE_STATIONS) return void window.setTimeout(() => setStationsOpen(true), 0)
                          if (v !== NEW_STATION) return setDraft({ ...draft, station: v })
                          // Bir tık sonra: liste kapanırken odağı kendi düğmesine
                          // geri alıyor, pencere ondan sonra açılsın ki yazı
                          // kutusu odakta kalsın.
                          window.setTimeout(() => setPrompt({
                            title: 'Yeni tezgah',
                            body: 'Bu ürün fişte bu adla ayrı basılır. Yazıcıya ya da mutfak ekranına Ayarlar\'dan atanabilir.',
                            label: 'Tezgah adı',
                            placeholder: 'Örn. Çiğ Köfte Tezgahı',
                            confirmLabel: 'Seç',
                            onConfirm: (name) => {
                              const clean = name.trim().slice(0, 40)
                              if (!clean) return
                              // Var olanla aynı adı farklı yazmasın: büyük/küçük harf aynı sayılıyor.
                              const same = knownStations.find(
                                (s) => s.toLocaleLowerCase('tr') === clean.toLocaleLowerCase('tr'),
                              )
                              setDraft((d) => (d ? { ...d, station: same ?? clean } : d))
                            },
                          }), 0)
                        }}
                        options={[
                          { value: '', label: fromCat, hint: 'kategoriden' },
                          ...others.map((s) => ({ value: s, label: s })),
                          { value: NEW_STATION, label: 'Yeni tezgah', tone: 'success', icon: <PlusIcon /> },
                          { value: MANAGE_STATIONS, label: 'Tezgahları düzenle', tone: 'warning', icon: <PencilIcon /> },
                        ]}
                      />
                    </div>
                  )
                })()}
                <button type="button" className="menu-modal__x" onClick={closeDraft} aria-label="Kapat">
                  <XIcon />
                </button>
              </div>
            </header>

            <div className="menu-modal__cols">
              <aside className="menu-modal__side">
                {/* Fotoğraf: büyük kare alan, dokununca seçiliyor. */}
                <button
                  type="button"
                  className={`pphoto ${draft.imageUrl ? 'has-img' : ''}`}
                  onClick={() => photoOn && fileInput.current?.click()}
                  disabled={!photoOn || uploading}
                  aria-label={draft.imageUrl ? 'Fotoğrafı değiştir' : 'Fotoğraf ekle'}
                >
                  {draft.imageUrl ? (
                    <img src={draft.imageUrl} alt="" />
                  ) : (
                    <span className="pphoto__empty">
                      <ImageIcon />
                      <b>{uploading ? 'Yükleniyor…' : photoOn ? 'Fotoğraf ekle' : 'Fotoğraf'}</b>
                      <small>
                        {photoOn
                          ? 'Kare ya da yatay, ürünü ortalayan bir fotoğraf'
                          : 'Görselli menü modülüyle açılır'}
                      </small>
                    </span>
                  )}
                  {uploading && draft.imageUrl && <span className="pphoto__busy">Yükleniyor…</span>}
                </button>
                {photoOn && draft.imageUrl && !uploading && (
                  <div className="pphoto__actions">
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => fileInput.current?.click()}>
                      Değiştir
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm menu-photo__remove"
                      onClick={() => {
                        if (draft.imageUrl !== draft.savedImageUrl) void deleteMenuImage(draft.imageUrl)
                        setDraft({ ...draft, imageUrl: null })
                      }}
                    >
                      Kaldır
                    </button>
                  </div>
                )}
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => void onPickImage(e.target.files?.[0])}
                />

                {/* Seçenek kartları (19 Eki 2026, kullanıcı): yeşil tik özelliği
                    açar ve kapatır; açık bir özelliğin kartına basmak sağda onun
                    sekmesini gösterir. Kapalı karta basmak bir şey açmaz. */}
                <div className="popts" role="group" aria-label="Ürün seçenekleri">
                  <OptCard
                    on={draft.active}
                    onToggle={(v) => setDraft({ ...draft, active: v })}
                    title="Kasada görünsün"
                    sub="Kapalıyken satışa çıkmaz"
                  />
                  {weighOn && (
                    <OptCard
                      on={draft.byWeight}
                      onToggle={(v) => {
                        setDraft({ ...draft, byWeight: v })
                        showTab(v ? 'weigh' : null, 'weigh')
                      }}
                      selected={tab === 'weigh'}
                      onShow={() => setTab('weigh')}
                      title="Kiloyla satılır"
                      sub="Tartı ve kap ayarı"
                    />
                  )}
                  {/* Porsiyonlu ürün (3 Kas 2026): kiloyla satılanda anlamsız. */}
                  {!draft.byWeight && (
                    <OptCard
                      on={draft.portionsOn}
                      onToggle={(v) => {
                        // Açılınca "1,5 Porsiyon" hazır, fiyatı 1,5 katı: çoğu işletme bunu ister.
                        const base = liraToKurus(draft.lira)
                        const first =
                          v && draft.portions.length === 0
                            ? [newPortionRow('1,5 Porsiyon', base ? kurusToLiraInput(Math.round((base * 1.5) / 100) * 100) : '')]
                            : draft.portions
                        setDraft({ ...draft, portionsOn: v, portions: first })
                        showTab(v ? 'portions' : null, 'portions')
                      }}
                      selected={tab === 'portions'}
                      onShow={() => setTab('portions')}
                      title="Porsiyonlu ürün"
                      sub="1 ve 1,5 porsiyon, ayrı fiyat"
                    />
                  )}
                  {/* Paketinde yoksa kilitli (29 Eki 2026); önceden stoklanmış ürün
                      kapatılabilsin diye açıkken kart normal kalıyor. */}
                  {stockOn || draft.tracked ? (
                    <OptCard
                      on={draft.tracked}
                      onToggle={(v) => {
                        setDraft({ ...draft, tracked: v })
                        showTab(v ? 'stock' : null, 'stock')
                      }}
                      selected={tab === 'stock'}
                      onShow={() => setTab('stock')}
                      title="Stoklu ürün"
                      sub="Satıldıkça düşer, bitince Tükendi olur"
                    />
                  ) : (
                    <LockedOptCard feature="stok" title="Stoklu ürün" onClick={() => askUpgrade('stok')} />
                  )}
                  {/* Maliyet (18 Eki 2026): yalnız patron. */}
                  {isOwner && !costOn && !draft.costOn && (
                    <LockedOptCard feature="maliyet" title="Maliyet ve kar" onClick={() => askUpgrade('maliyet')} />
                  )}
                  {isOwner && (costOn || draft.costOn) && (
                    <OptCard
                      on={draft.costOn}
                      onToggle={(v) => {
                        setDraft({ ...draft, costOn: v })
                        showTab(v ? 'cost' : null, 'cost')
                      }}
                      selected={tab === 'cost'}
                      onShow={() => setTab('cost')}
                      title="Maliyet ve kar"
                      sub="Tek tutar ya da hammaddeden"
                    />
                  )}
                  {/* Most items are happy with their category's list; this is
                      for the one that isn't, like a drink in a food section. */}
                  <OptCard
                    on={draft.notesOverridden}
                    onToggle={(v) => {
                      // Açılınca kategorinin notları hazır gelir (kullanıcı, 20 Eki
                      // 2026): istemediğini çıkarıp ürüne özel ekliyor. Eskiden
                      // liste boş başlıyordu, hepsini yeniden yazmak gerekiyordu.
                      const cat = categories.find((c) => c.id === draft.categoryId)
                      const inherited = cat?.noteOptions ?? DEFAULT_NOTE_OPTIONS
                      setDraft({
                        ...draft,
                        notesOverridden: v,
                        notes: v && draft.notes.length === 0 ? [...inherited] : draft.notes,
                      })
                      showTab(v ? 'notes' : null, 'notes')
                    }}
                    selected={tab === 'notes'}
                    onShow={() => setTab('notes')}
                    title="Ürüne özel notlar"
                    sub="Kategorinin notlarını çıkar, yenisini ekle"
                  />
                </div>
              </aside>

              <div className="menu-modal__main">
                {/* Ürün adı sağ sütunun başında, başlık gibi (19 Eki 2026). */}
                <input
                  className="menu-modal__title"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Ürün adı"
                  aria-label="Ürün adı"
                  required
                />

                <div className="menu-modal__pair">
                  <label>
                    Kategori
                    <Select
                      value={draft.categoryId}
                      onChange={(v) => setDraft({ ...draft, categoryId: v })}
                      aria-label="Kategori"
                      options={categories.map((c) => ({ value: c.id, label: c.name }))}
                    />
                  </label>
                  <label>
                    {draft.byWeight ? 'Kilo fiyatı (TL / kg)' : 'Fiyat (TL)'}
                    <input
                      value={draft.lira}
                      onChange={(e) => setDraft({ ...draft, lira: amountInput(e.target.value) })}
                      inputMode="decimal"
                      placeholder="150"
                      required
                    />
                  </label>
                </div>

                {/* "Açıklama" tek başına nerede görüneceğini söylemiyor, ve
                    cevap önemli: müşterinin okuduğu yer. */}
                <label>
                  Açıklama (isteğe bağlı)
                  <input
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="İçindekiler…"
                  />
                  <small className="menu-modal__hint">
                    QR menüde ürünün altında müşteriye görünür. İçindekiler, porsiyon ya da acı olup
                    olmadığı gibi sorulan şeyler.
                  </small>
                </label>

                {/* Barkodlar (16 Eki 2026): kutu seçiliyken okuyucuyla okutmak
                    yetiyor, okuyucu Enter'a basınca kutucuk olarak ekleniyor. */}
                <div className="pbarcodes">
                  <span className="pbarcodes__label">Barkod (isteğe bağlı)</span>
                  <div className="pbarcodes__row">
                  <div className="pbarcodes__box">
                    {draft.barcodes.map((b) => (
                      <span key={b} className="pbarcodes__chip">
                        {b}
                        <button
                          type="button"
                          aria-label={`${b} barkodunu kaldır`}
                          onClick={() => setDraft({ ...draft, barcodes: draft.barcodes.filter((x) => x !== b) })}
                        >
                          <XIcon />
                        </button>
                      </span>
                    ))}
                    <input
                      value={draft.barcodeInput}
                      onChange={(e) => setDraft({ ...draft, barcodeInput: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Formu göndermesin: okuyucu her okutmada Enter'a basıyor.
                          e.preventDefault()
                          addBarcode()
                        }
                      }}
                      placeholder={draft.barcodes.length ? 'Başka barkod ekle' : 'Okutun ya da yazıp Enter'}
                      aria-label="Barkod"
                      autoComplete="off"
                    />
                  </div>
                    <button
                      type="button"
                      className="pbarcodes__cam"
                      onClick={(e) => {
                        // Pencere kapanınca odak düğmede kalıp kutuyu seçili göstermesin.
                        e.currentTarget.blur()
                        setScanningBarcode(true)
                      }}
                      aria-label="Kamerayla okut"
                      title="Kamerayla okut"
                    >
                      <ScanBarcodeIcon />
                    </button>
                  </div>
                  <small className="menu-modal__hint">
                    Kasada okutunca ürün sepete düşer. Aynı ürünün farklı barkodları da eklenebilir.
                  </small>
                </div>

                {scanningBarcode && (
                  <CameraScanner onCode={scanBarcodeToDraft} onClose={() => setScanningBarcode(false)} />
                )}

                {/* Porsiyonlar (3 Kas 2026): ürüne basınca garsonda, kasada ve QR
                    menüde bunlar fiyatıyla çıkar. İlk satır ürünün kendi fiyatı. */}
                {draft.portionsOn && !draft.byWeight && tab === 'portions' && (
                  <section className="psec">
                    <h3>Porsiyonlar</h3>
                    <div className="pcost__rows">
                      <div className="pport__row">
                        <input value={BASE_PORTION} readOnly tabIndex={-1} aria-label="Porsiyon adı" className="pport__fixed" />
                        <div className="pport__price">
                          <input
                            value={draft.lira}
                            onChange={(e) => setDraft({ ...draft, lira: amountInput(e.target.value) })}
                            inputMode="decimal"
                            placeholder="150"
                            aria-label={`${BASE_PORTION} fiyatı`}
                          />
                          <span>₺</span>
                        </div>
                        <span />
                      </div>
                      {draft.portions.map((r) => {
                        const update = (patch: Partial<PortionRow>) =>
                          setDraft({ ...draft, portions: draft.portions.map((x) => (x.key === r.key ? { ...x, ...patch } : x)) })
                        return (
                          <div className="pport__row" key={r.key}>
                            <input
                              value={r.name}
                              onChange={(e) => update({ name: e.target.value })}
                              placeholder="1,5 Porsiyon"
                              aria-label="Porsiyon adı"
                            />
                            <div className="pport__price">
                              <input
                                value={r.lira}
                                onChange={(e) => update({ lira: amountInput(e.target.value) })}
                                inputMode="decimal"
                                placeholder="225"
                                aria-label="Porsiyon fiyatı"
                              />
                              <span>₺</span>
                            </div>
                            <button
                              type="button"
                              className="pcost__del"
                              onClick={() => setDraft({ ...draft, portions: draft.portions.filter((x) => x.key !== r.key) })}
                              aria-label="Porsiyonu sil"
                              title="Porsiyonu sil"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm pcost__add"
                      onClick={() => setDraft({ ...draft, portions: [...draft.portions, newPortionRow()] })}
                    >
                      <PlusIcon />
                      Porsiyon ekle
                    </button>
                    <small className="menu-modal__hint">
                      Garson, kasa ve QR menüde ürüne basınca porsiyonlar fiyatıyla çıkar. Fişte ve
                      mutfakta "{draft.name.trim() || 'Ürün'} (1,5 Porsiyon)" diye yazar.
                    </small>
                  </section>
                )}

                {/* Stok (16 Eki 2026): soldaki "Stoklu ürün" açıkken; satıldıkça
                    veritabanında düşüyor, bitince ürün Tükendi oluyor. */}
                {draft.tracked && tab === 'stock' && (
                <section className="psec">
                  <h3>Stok</h3>
                  <div className="menu-modal__pair">
                    <label>
                      {draft.byWeight ? 'Stok (kg)' : 'Stok (adet)'}
                      <input
                        value={draft.stock}
                        onChange={(e) => setDraft({ ...draft, stock: amountInput(e.target.value) })}
                        inputMode="decimal"
                        placeholder={draft.byWeight ? 'örn. 5' : 'örn. 24'}
                      />
                    </label>
                    <label>
                      Az kaldı uyarısı
                      <input
                        value={draft.stockMin}
                        onChange={(e) => setDraft({ ...draft, stockMin: amountInput(e.target.value) })}
                        inputMode="decimal"
                        placeholder="Yok"
                      />
                    </label>
                  </div>
                  <small className="menu-modal__hint">
                    Satıldıkça kendiliğinden düşer; bitince ürün Tükendi olur, yeniden stok girince
                    kalkar. Uyarı sayısına inince kasada ve menüde "Az kaldı" görünür.
                  </small>
                </section>
                )}

                {/* Maliyet (18 Eki 2026), yalnız patron. Tek tutar ya da ortak
                    hammadde listesinden; hammadde fiyatı değişince maliyet de değişir. */}
                {isOwner && draft.costOn && tab === 'cost' && (() => {
                  const per = draft.byWeight ? '1 kg' : '1 adet'
                  const rowCosts = draft.recipe.map((r) => {
                    const q = parseQty(r.qty)
                    return q === null ? null : lineCostKurus(q, materialMap.get(r.materialId))
                  })
                  const live =
                    draft.costMode === 'direct'
                      ? liraToKurus(draft.costLira)
                      : rowCosts.some((c) => c !== null)
                        ? rowCosts.reduce<number>((a, c) => a + (c ?? 0), 0)
                        : null
                  const price = liraToKurus(draft.lira)
                  const m = live !== null && price !== null ? margin(price, live) : null
                  const missing = draft.recipe.some((r) => r.materialId && !materialMap.has(r.materialId))
                  return (
                    <section className="psec pcost">
                      {/* Hammaddeler listesi buradan (kullanıcı, 20 Eki 2026): eskiden
                          Menü Yönetimi'nin üstünde ayrı bir düğmeydi. */}
                      <div className="pcost__head">
                        <h3>Maliyet</h3>
                        <button
                          type="button"
                          className="pcost__mat"
                          onClick={() => setMaterialsOpen(true)}
                          title="Hammaddeler"
                          aria-label="Hammaddeler listesini aç"
                        >
                          <LogsIcon />
                        </button>
                      </div>
                      <div className="pcost__tabs" role="group" aria-label="Maliyet nasıl girilsin">
                        <button
                          type="button"
                          className={`pcost__tab ${draft.costMode === 'direct' ? 'is-on' : ''}`}
                          aria-pressed={draft.costMode === 'direct'}
                          onClick={() => setDraft({ ...draft, costMode: 'direct' })}
                        >
                          Tek tutar
                        </button>
                        <button
                          type="button"
                          className={`pcost__tab ${draft.costMode === 'recipe' ? 'is-on' : ''}`}
                          aria-pressed={draft.costMode === 'recipe'}
                          onClick={() => setDraft({ ...draft, costMode: 'recipe' })}
                        >
                          Hammaddeden
                        </button>
                      </div>

                      {draft.costMode === 'direct' ? (
                        <label>
                          {per} maliyeti (TL)
                          <input
                            value={draft.costLira}
                            onChange={(e) => setDraft({ ...draft, costLira: amountInput(e.target.value) })}
                            inputMode="decimal"
                            placeholder={draft.byWeight ? 'örn. 148' : 'örn. 85'}
                          />
                          <small className="menu-modal__hint">Hammaddeye bölmeden, ürünün size kaça geldiği.</small>
                        </label>
                      ) : (
                        <>
                          <small className="menu-modal__hint">
                            {per} ürün için ne kadar hammadde gidiyor? Fiyatlar Hammaddeler listesinden
                            gelir; orada değişince maliyet de kendiliğinden değişir.
                          </small>
                          <div className="pcost__rows">
                            {draft.recipe.map((r, i) => {
                              const mat = materialMap.get(r.materialId)
                              return (
                                <div className="pcost__row" key={r.key}>
                                  <Select
                                    value={r.materialId}
                                    onChange={(v) =>
                                      v === '__new'
                                        ? setDraft({ ...draft, newMat: { rowKey: r.key, name: '', unit: 'kg', lira: '' } })
                                        : updateRow(r.key, { materialId: v })
                                    }
                                    aria-label="Hammadde"
                                    options={[
                                      { value: '', label: 'Hammadde seçin' },
                                      ...materials.map((x) => ({
                                        value: x.id,
                                        label: x.name,
                                        hint: `${formatTRY(x.priceKurus)} / ${UNIT_LABEL[x.unit]}`,
                                      })),
                                      { value: '__new', label: '+ Yeni hammadde…' },
                                    ]}
                                  />
                                  <div className="pcost__qty">
                                    <input
                                      value={r.qty}
                                      onChange={(e) => updateRow(r.key, { qty: qtyInput(e.target.value) })}
                                      inputMode="decimal"
                                      placeholder={mat?.unit === 'adet' ? '1' : '0,180'}
                                      aria-label="Miktar"
                                    />
                                    <span>{mat ? UNIT_LABEL[mat.unit] : ''}</span>
                                  </div>
                                  <span className="pcost__line">{rowCosts[i] !== null ? formatTRY(rowCosts[i]!) : ''}</span>
                                  <button
                                    type="button"
                                    className="pcost__del"
                                    onClick={() => removeRow(r.key)}
                                    aria-label="Satırı sil"
                                    title="Satırı sil"
                                  >
                                    <TrashIcon />
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                          {missing && <p className="pcost__warn">Silinmiş bir hammadde var, maliyete katılmıyor.</p>}
                          {draft.newMat && (
                            <div className="pcost__new">
                              <input
                                value={draft.newMat.name}
                                onChange={(e) => setDraft({ ...draft, newMat: { ...draft.newMat!, name: e.target.value } })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    void onAddMaterial()
                                  }
                                }}
                                placeholder="Hammadde adı (Kıyma)"
                                aria-label="Hammadde adı"
                              />
                              <Select
                                value={draft.newMat.unit}
                                onChange={(v) => setDraft({ ...draft, newMat: { ...draft.newMat!, unit: v as MaterialUnit } })}
                                aria-label="Birim"
                                options={[
                                  { value: 'kg', label: 'kg' },
                                  { value: 'adet', label: 'adet' },
                                  { value: 'lt', label: 'lt' },
                                ]}
                              />
                              <input
                                value={draft.newMat.lira}
                                onChange={(e) =>
                                  setDraft({ ...draft, newMat: { ...draft.newMat!, lira: amountInput(e.target.value) } })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    void onAddMaterial()
                                  }
                                }}
                                inputMode="decimal"
                                placeholder={`TL / ${UNIT_LABEL[draft.newMat.unit]}`}
                                aria-label="Birim fiyatı"
                              />
                              <button type="button" className="btn btn--primary btn--sm" onClick={() => void onAddMaterial()}>
                                Ekle
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setDraft({ ...draft, newMat: null })}
                              >
                                Vazgeç
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm pcost__add"
                            onClick={() => setDraft({ ...draft, recipe: [...draft.recipe, newRow()] })}
                          >
                            <PlusIcon />
                            Hammadde ekle
                          </button>
                        </>
                      )}

                      {live !== null && (
                        <div className="pcost__sum">
                          <span>
                            Maliyet <b>{formatTRY(live)}</b>
                            {draft.byWeight && ' / kg'}
                          </span>
                          {price !== null && (
                            <span>
                              Satış <b>{formatTRY(price)}</b>
                            </span>
                          )}
                          {m && (
                            <span className={m.profitKurus < 0 ? 'is-neg' : 'is-pos'}>
                              Kar <b>{formatTRY(m.profitKurus)}</b>
                              {m.pct !== null && ` (%${Math.round(m.pct)})`}
                            </span>
                          )}
                        </div>
                      )}
                    </section>
                  )
                })()}

                {draft.byWeight && tab === 'weigh' && (
                  <section className="psec">
                    <h3>Tartı</h3>
                    <div className="menu-modal__tare">
                      <label>
                        Kap (dara)
                        <Select
                          value={draft.tareKey}
                          onChange={(v) => setDraft({ ...draft, tareKey: v })}
                          aria-label="Kap"
                          options={[
                            { value: '', label: 'Kap yok', hint: 'dara 0 g' },
                            ...knownTares.map((t) => ({
                              value: tareKeyOf(t),
                              label: t.name,
                              hint: `${t.gr} g`,
                            })),
                            { value: 'new', label: '+ Yeni kap ekle…' },
                          ]}
                        />
                      </label>
                      {draft.tareKey === 'new' && (
                        <div className="menu-modal__tare-new">
                          <input
                            value={draft.tareName}
                            onChange={(e) => setDraft({ ...draft, tareName: e.target.value })}
                            placeholder="Kap adı (Porselen tabak)"
                          />
                          <input
                            value={draft.tareGr}
                            onChange={(e) =>
                              setDraft({ ...draft, tareGr: e.target.value.replace(/[^\d,.]/g, '') })
                            }
                            inputMode="numeric"
                            placeholder="Gram"
                          />
                        </div>
                      )}
                      <small className="menu-modal__hint">
                        Tartımda kabın ağırlığı otomatik düşülür. Tartarken başka kap seçilebilir.
                      </small>
                    </div>
                  </section>
                )}

                {draft.notesOverridden && tab === 'notes' && (
                  <section className="psec">
                    <h3>Sipariş notları</h3>
                    <small className="pnotes__hint">
                      Kategoriden gelenler listede. Bu üründe istemediğini × ile çıkarın, ürüne özel notu
                      yazıp ekleyin.
                    </small>
                    <TagListEditor
                      value={draft.notes}
                      onChange={(notes) => setDraft({ ...draft, notes })}
                      placeholder="Soğansız"
                      emptyText="Bu üründe hazır not çıkmaz."
                    />
                  </section>
                )}
              </div>
            </div>

            {formError && <p className="menu-modal__error">{formError}</p>}

            <div className="menu-modal__buttons">
              <button type="button" className="btn btn--ghost" onClick={closeDraft}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy || uploading}>
                {busy ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {materialsOpen && tenantId && (
        <MaterialsDialog
          tenantId={tenantId}
          materials={materials}
          costs={costMap}
          onClose={() => setMaterialsOpen(false)}
        />
      )}

      {/* Every question this screen asks, in the app's own voice rather than
          the browser's. */}
      {stationsOpen && (
        <StationsDialog
          stations={stationUses()}
          onDelete={onDeleteStation}
          onClose={() => setStationsOpen(false)}
        />
      )}
      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
      {prompt && <PromptDialog {...prompt} onClose={() => setPrompt(null)} />}
      {stationFor && (
        <StationDialog
          categoryName={stationFor.name}
          current={stationFor.station || DEFAULT_STATION}
          known={knownStations}
          skipped={skipped}
          onSave={(station, print) => void saveStation(stationFor, station, print)}
          onManage={() => {
            setStationFor(null)
            setStationsOpen(true)
          }}
          onClose={() => setStationFor(null)}
        />
      )}

      {noteEdit && (
        <NoteOptionsDialog
          title={`"${noteEdit.name}" not seçenekleri`}
          initial={noteEdit.noteOptions ?? DEFAULT_NOTE_OPTIONS}
          onCancel={() => setNoteEdit(null)}
          onConfirm={(next) => void onSaveNotes(noteEdit, next)}
        />
      )}
      {printing && tenantId && (
        <MenuPrintDialog
          tenantId={tenantId}
          tenantName={tenantName}
          categories={categories}
          products={products}
          onClose={() => setPrinting(false)}
        />
      )}

      {importing && tenantId && (
        <MenuImportDialog
          tenantId={tenantId}
          categories={categories}
          products={products}
          onCancel={() => setImporting(false)}
          onDone={() => setImporting(false)}
        />
      )}
    </div>
  )
}

type OptTab = 'weigh' | 'portions' | 'stock' | 'cost' | 'notes'

/**
 * Ürün penceresindeki seçenek kartı (19 Eki 2026): tik özelliği açar/kapatır,
 * karta basmak (özellik açıksa ve bölümü varsa) sağda sekmesini gösterir.
 */
function OptCard({
  on,
  onToggle,
  selected = false,
  onShow,
  title,
  sub,
}: {
  on: boolean
  onToggle: (on: boolean) => void
  selected?: boolean
  onShow?: () => void
  title: string
  sub: string
}) {
  const canShow = on && onShow
  return (
    <div className={`popt ${on ? 'is-on' : ''} ${selected ? 'is-sel' : ''} ${canShow ? 'has-tab' : ''}`}>
      <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} aria-label={title} />
      <button
        type="button"
        className="popt__body"
        onClick={() => canShow && onShow()}
        disabled={!canShow}
        aria-pressed={canShow ? selected : undefined}
      >
        <b>{title}</b>
        <small>{sub}</small>
      </button>
    </div>
  )
}

/** Paketinde olmayan seçenek (29 Eki 2026): kilitli kart, basınca yükseltme penceresi. */
function LockedOptCard({ feature, title, onClick }: { feature: FeatureKey; title: string; onClick: () => void }) {
  const plan = planFor(feature)
  return (
    <div className="popt is-locked">
      <span className="popt__lock" aria-hidden="true">
        <LockIcon />
      </span>
      <button type="button" className="popt__body" onClick={onClick}>
        <b>{title}</b>
        <small>{plan ? `${plan.name} paketinde` : 'Ek modül'}</small>
      </button>
    </div>
  )
}
